import React, { FC, ReactNode, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { makeStyles } from '@material-ui/styles';

import imagePlaceholder from '../../assets/imagePlaceholder.png';
import { ProgressLoader } from 'components/UI/ProgressLoader';
import { IonIcon } from 'components/UI/IonIcon';
import { Button } from 'components/shadcn/button';
import { useAppDispatch, useAppSelector } from '../../store';
import { useLanguage } from '../../languages/languageContext';

import {
  uploadDocument,
  uploadVideo,
  uploadImage,
  selectMediaState,
  mediaActions,
} from '../../store/reducers/mediaSlice';
import FilesLoader from 'components/Shared/FillesLoader';
import { showResponse } from 'store/actions/response';
import PDFToJPGConverter from 'utilities/PDFToJPGConverter';
import { fileType } from 'store/reducers/attachments';
import { PaperclipIcon } from 'lucide-react';
import DefaultModal from './DefaultModal';

const useStyles = makeStyles({
  container: {
    marginBottom: '15px',
  },
  deleteButton: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    zIndex: 20,
    width: '1.5rem',
    height: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(128, 128, 128, 0.5)',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    backdropFilter: 'blur(4px)',
    transition: 'background-color 0.2s, transform 0.1s',
    '&:hover': {
      backgroundColor: 'rgba(128, 128, 128, 0.7)',
      transform: 'scale(1.05)',
    },
  },
  backdrop: {
    width: '100%',
    height: '10rem',
    borderRadius: 20,
    borderWidth: 1,
    border: 'dashed',
    borderColor: 'gray',
    backgroundColor: `#e0e0e030`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
  },
  dragover: {
    backgroundColor: `#e0e0e050`,
  },
  image: {
    height: '8rem',
    objectFit: 'cover',
  },
  placeholder: {
    mixBlendMode: 'multiply',
    zIndex: -1,
  },
  buttonLabel: {
    cursor: 'pointer',
  },
  errorText: {
    minHeight: '1rem',
    marginLeft: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    color: 'red',
  },
});

export const UploadMedia: FC<{
  receiver?: any;
  slideToBottom?: boolean;
  viewType?: 'default' | 'button' | 'dropzone' | 'singleImageInput' | 'multiImageInput';
  addPictureDisabled?: boolean;
  addPDFDisabled?: boolean;
  addVideoDisabled?: boolean;
  allFormats?: boolean;
  isPrivate?: boolean;
  additionalBtn?: ReactNode[];
  multiple?: boolean;
  text?: string;
  lables?: { addLabel?: string; editLabel?: string };
  editImage?: string;
  disabled?: boolean;
}> = ({
  receiver,
  slideToBottom = false,
  viewType = 'default',
  addPictureDisabled = false,
  addPDFDisabled = false,
  addVideoDisabled = false,
  allFormats = false,
  isPrivate = false,
  additionalBtn,
  multiple = true,
  text,
  lables,
  editImage,
  disabled = false,
}) => {
  const addLabel = lables?.addLabel ?? 'Add Image';
  const editLabel = lables?.editLabel ?? 'Edit Image';
  const dispatch = useAppDispatch();
  const { responseHandler, global, documentsScreen } = useLanguage();
  const [showLoader, setShowLoader] = useState(false);
  const { documents, videos, images } = useAppSelector(selectMediaState);
  const styles = useStyles({});
  const [error, setError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const handleDisabledClick = () => {
    setShowWarning(true);
  };

  const [pdfs, setPdfs] = useState([]);
  const [processing, setProcessing] = useState<fileType | null>(null);

  const imageFormats = ['image/jpeg', 'image/png'];

  useEffect(() => {
    if (!processing && pdfs.length > 0) {
      const next = pdfs[0];
      setProcessing(next);
      setPdfs((q) => q.slice(1));
    }
  }, [pdfs, processing]);

  useEffect(() => {
    const allAttachment = [...(documents || []), ...(videos || []), ...(images || [])];
    const isAttachmentLoading = allAttachment.every((attachment: fileType) => attachment?.size);
    dispatch(mediaActions.setLoadingAllAttachments(isAttachmentLoading));
  }, [documents, images, videos]);

  const onDropFiles = (files: File[]) => {
    setShowLoader(false);
    files.forEach((file) => {
      if (file.size > 2048 * 1e6) {
        dispatch(showResponse({ message: responseHandler.maximalVideoSize }));
        return;
      }
      if (file.type.includes('video')) {
        dispatch(uploadVideo({ file, receiverId: receiver?._id, isPrivate }));
        return;
      }
      if (file.type.includes('image')) {
        dispatch(uploadImage({ file, receiverId: receiver?._id, isPrivate }));
        return;
      }
      if (file.type === 'application/pdf') {
        const formdata = new FormData();
        formdata.append('document', file);
        const payload: Partial<fileType> = {
          size: file.size,
          mimetype: file.type,
          fileName: file.name,
          renderUrl: URL.createObjectURL(file),
          formdata,
        };
        setPdfs((q) => [...q, payload]);
        dispatch(mediaActions.setLoadingAllAttachments(true));
        return;
      }

      const formdata = new FormData();
      formdata.append('document', file);
      dispatch(uploadDocument({ formdata, receiverId: receiver?._id, isPrivate }));
    });
  };

  const handleFinishProcessing = () => {
    setProcessing(null);
  };

  if (viewType === 'singleImageInput') {
    const formats = ['image/jpeg', 'image/png'];

    const deleteAttachment = () => {
      dispatch(mediaActions.deleteAttachment({ type: 'images', id: 0 }));
    };

    const onDropSingleImage = (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (formats.includes(file.type)) {
          deleteAttachment();
          dispatch(uploadImage({ file }));
          setError(null);
        } else {
          setError(global?.invalidImageFormat);
        }
      }
    };

    const currentImage = images[0]?.renderUrl || images[0]?.url || editImage || imagePlaceholder;

    return (
      <>
        <Dropzone accept={imageFormats} onDrop={onDropSingleImage} multiple={false}>
          {({ getRootProps, getInputProps }) => (
            <div>
              <div className={styles.container}>
                <input id="file" type="file" {...getInputProps()} />
                <Button>
                  <label htmlFor="file" className={styles.buttonLabel}>
                    {images[0] || editImage ? editLabel : addLabel}
                  </label>
                </Button>
              </div>
              <div
                {...getRootProps({
                  onClick: (e) => {
                    if ((e.target as HTMLElement).closest(`.${styles.deleteButton}`)) {
                      e.preventDefault();
                    }
                  },
                })}
                className={`${styles.backdrop} ${error ? styles.dragover : ''}`}
              >
                <div>
                  {(images[0] || editImage) && (
                    <div
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteAttachment();
                      }}
                    >
                      <IonIcon name="trash" color="red" />
                    </div>
                  )}
                  <img
                    className={`${styles.image} ${!images[0] && !editImage && styles.placeholder}`}
                    src={currentImage}
                    alt={global.selectedOrPlaceholder}
                  />
                  <ProgressLoader loading={!!images[0]?.progress} progress={images[0]?.progress} />
                </div>
              </div>
            </div>
          )}
        </Dropzone>
        <div className={styles.errorText}>{error}</div>
      </>
    );
  }

  if (viewType === 'multiImageInput') {
    const formats = ['image/jpeg', 'image/png'];

    const deleteAttachment = (index: number) => {
      dispatch(mediaActions.deleteAttachment({ type: 'images', id: index }));
    };

    const onDropMultipleImages = (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const invalidFiles = acceptedFiles.filter((f) => !formats.includes(f.type));
      if (invalidFiles.length > 0) {
        setError(global?.invalidImageFormat);
        return;
      }

      setError(null);
      acceptedFiles.forEach((file) => {
        dispatch(uploadImage({ file }));
      });
    };

    const currentImages =
      images.length > 0 ? images.map((img) => img.renderUrl || img.url) : editImage ? [editImage] : [imagePlaceholder];

    return (
      <>
        <Dropzone accept={imageFormats} onDrop={onDropMultipleImages} multiple>
          {({ getRootProps, getInputProps }) => (
            <div className={styles.container}>
              <input id="file" type="file" {...getInputProps()} />
              <Button>
                <label htmlFor="file" className={styles.buttonLabel}>
                  {images.length > 0 || editImage ? editLabel : addLabel}
                </label>
              </Button>
              <div
                {...getRootProps({
                  onClick: (e) => {
                    if ((e.target as HTMLElement).closest(`.${styles.deleteButton}`)) {
                      e.preventDefault();
                    }
                  },
                })}
                className={`${styles.backdrop} ${error ? styles.dragover : ''}`}
                style={{
                  marginTop: '12px',
                }}
              >
                {images.length === 0 && !editImage ? (
                  <img
                    className={`${styles.image} ${styles.placeholder}`}
                    src={imagePlaceholder}
                    alt={global.selectedOrPlaceholder}
                  />
                ) : (
                  currentImages.map((src, idx) => (
                    <div key={idx} className="relative m-1 inline-block">
                      <div
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAttachment(idx);
                        }}
                      >
                        <IonIcon name="trash" color="red" />
                      </div>
                      <img
                        className={`${styles.image} ${!images[idx] && !editImage && styles.placeholder}`}
                        src={src}
                        alt={global.selectedOrPlaceholder}
                      />
                      <ProgressLoader loading={!!images[idx]?.progress} progress={images[idx]?.progress} />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </Dropzone>
        <div className={styles.errorText}>{error}</div>
      </>
    );
  }

  return (
    <>
      {disabled ? (
        <div onClick={handleDisabledClick} style={{ cursor: 'pointer' }}>
          <PaperclipIcon color="#6B7280" size={22} />
        </div>
      ) : (
        <FilesLoader
          onDrop={onDropFiles}
          showLoader={showLoader}
          loaderHandler={() => setShowLoader(!showLoader)}
          slideToBottom={slideToBottom}
          viewType={viewType}
          addPictureDisabled={addPictureDisabled}
          addPDFDisabled={addPDFDisabled}
          addVideoDisabled={addVideoDisabled}
          allFormats={allFormats}
          additionalBtn={additionalBtn}
          multiple={multiple}
          text={text}
        />
      )}
      {processing && (
        <PDFToJPGConverter
          key={processing.fileName}
          pdf={processing}
          setPdfs={() => handleFinishProcessing()}
          isPrivate={isPrivate}
          receiverId={receiver?._id}
        />
      )}
      <DefaultModal
        showModal={showWarning}
        onClose={() => setShowWarning(false)}
        modalTitle={documentsScreen.onlyAdminsCanUploadTitle}
        message={documentsScreen.onlyAdminsCanUploadMessage}
        children={null}
      />
    </>
  );
};
